import {
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { DEBOUNCE_DELAY } from 'utils/constants/app/magic-numbers-app';
import { FAVORITE } from 'utils/constants/casino/casino-element-names';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { GLOBALS } from 'utils/generic/global-variables';
import { lazy } from 'utils/generic/lazy';
import {
  getCasinoParams,
  mountPoint,
  sbRedirect
} from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { TCasinoCategoryItems } from 'interfaces/casino-data';
import {
  ENewCasinoGamesFetchingParamsKey,
  NewCasinoCategory,
  TNewCasinoDataWithCounts
} from 'interfaces/new-casino';
import { AFTER_SIGN_IN_ACTION_TYPES, AfterSignIn } from 'services/afterSignIn';
import { getCasinoGames } from 'services/casino/casino-fetch-games-query';
import { CategoriesHorizontalDesktopSkeleton } from './__partials/layouts/HorizontalDesktop/Skeleton';
import { CategoriesHorizontalMobileSkeleton } from './__partials/layouts/HorizontalMobile/Skeleton';
import { CategoriesVerticalSkeleton } from './__partials/layouts/Vertical/Skeleton';
import { saveCurrentLocation } from 'components/QueryRoute';
import { Viewport } from 'components/Viewport';
import { useOpenLoginPopup } from 'hooks/account/useOpenLoginPopup';
import { useDebounce } from 'hooks/useDebounce';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setSelectedCasinoCategoryId } from 'store/actions/casino-data';
import {
  setCasinoOriginalCategoriesSet,
  setCasinoOriginalCategoriesSetLeftSideBar,
  setCategoryCustomIds,
  setCategoryProviderIds,
  setLastPlayedCatSettings,
  setTotalCount
} from 'store/actions/new-casino';
import {
  getCasinoOriginalCategories,
  getEnhancedParams,
  getGamesConfigs,
  getLastPlayedCatSettings
} from 'store/selectors/new-casino';
import { getIsLoggedIn } from 'store/selectors/user-data';
import { useCasinoRedirect } from '../hooks/useCasinoRedirect';
import { useOptions } from '../hooks/useOptions';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const { CategoriesDesktop } = lazy(
  () =>
    import(
      /* webpackChunkName: "casino-categories-desktop" */ './__partials/views/Desktop'
    )
);

const { CategoriesMobile } = lazy(
  () =>
    import(
      /* webpackChunkName: "casino-categories-mobile" */ './__partials/views/Mobile'
    )
);

type TCasinoCategoriesProps = {
  configs: {
    isFirstCasinoElement?: boolean;
    categoryIds?: string;
    categoryCustomIds?: string;
    providerIds?: string;
    layout: 'horizontal' | 'vertical';
    gamesCount?: boolean;
    moduleId?: number;
    categoryItems?: TCasinoCategoryItems;
    lastPlayed?: boolean;
    lastPlayedCatType?: number;
    namesOnly: boolean;
  };
  fromSideBar?: boolean;
  isOpen?: boolean;
};

const casinoCategoriesDefaultProps: TCasinoCategoriesProps = {
  configs: {
    categoryIds: '',
    layout: 'horizontal',
    isFirstCasinoElement: true,
    gamesCount: true,
    categoryItems: {},
    lastPlayed: true,
    lastPlayedCatType: 0,
    namesOnly: false
  }
};

const THEME = 'colored';
const ENHANCED_PARAMS_CORRECT_KEYS_LENGTH = 3;
const CATEGORY_IDS: string[] = [];

export const CasinoCategories = ({
  configs: {
    categoryIds,
    layout,
    isFirstCasinoElement,
    providerIds,
    gamesCount,
    categoryItems,
    lastPlayed,
    lastPlayedCatType,
    categoryCustomIds,
    namesOnly
  },
  fromSideBar,
  isOpen
}: TCasinoCategoriesProps = casinoCategoriesDefaultProps): ReactElement => {
  const categoryElementId = useId();
  useOptions({
    shouldFetch: isFirstCasinoElement,
    providerIdsForCategories: providerIds
  });
  useCasinoRedirect(true, fromSideBar);
  const { t } = useTranslation();
  const categories = useSelector(getCasinoOriginalCategories);
  const lastPlayedCatSettings = useSelector(getLastPlayedCatSettings);
  const enhancedParams = useSelector(getEnhancedParams);
  const gamesConfigs = useSelector(getGamesConfigs);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const openLoginPopup = useOpenLoginPopup();

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const routeMatch = useSbRouteMatch(
    `${mountPoint(location.pathname, false, true)}/:category?`
  );

  useLayoutEffect(() => {
    CATEGORY_IDS.push(categoryElementId);

    return () => {
      const index = CATEGORY_IDS.indexOf(categoryElementId);

      if (index >= 0) {
        CATEGORY_IDS.splice(index, 1);
      }
    };
  }, [categoryElementId]);

  useEffect(() => {
    if (CATEGORY_IDS[0] !== categoryElementId) {
      return;
    }

    if (!routeMatch?.params?.category) {
      return;
    }

    if (!categories) {
      return;
    }

    const dataSpotCategories = categories.find(category => {
      return category.id === routeMatch.params.category;
    });

    dataSpotCategories &&
      window.__dataspot__?.trackCategory?.([dataSpotCategories.name]);
  }, [categories?.length, routeMatch?.params?.category, categoryElementId]);

  const LAYOUTS_SKELETON = useMemo(() => {
    return {
      horizontal: (
        <Viewport
          desktop={CategoriesHorizontalDesktopSkeleton}
          mobile={CategoriesHorizontalMobileSkeleton}
          innerProps={{ namesOnly }}
        />
      ),
      vertical: (
        <CategoriesVerticalSkeleton
          fromSideBar={fromSideBar}
          namesOnly={namesOnly}
          gamesCount={!!gamesCount}
        />
      )
    };
  }, [fromSideBar]);

  const SkeletonLayout = useMemo(() => {
    return LAYOUTS_SKELETON[layout];
  }, []);

  useEffect(() => {
    GLOBALS.casino.isCasinoCategoriesMounted = true;

    dispatch(
      setLastPlayedCatSettings({
        lastPlayed: lastPlayed ?? true,
        lastPlayedCatType: lastPlayedCatType ?? 0
      })
    );
  }, []);

  useLayoutEffect(() => {
    dispatch(setCategoryProviderIds(providerIds || ''));
  }, [providerIds]);

  const onCategoryClick = useCallback(
    (category: NewCasinoCategory) => {
      const handleCategoryClick = () => {
        return new Promise(resolve => {
          dispatch(setSelectedCasinoCategoryId(category.id));

          if (
            fromSideBar &&
            SpringConfigs.PAGE_URLS.casino &&
            !`${window.location.href}/`.includes(
              `${SpringConfigs.PAGE_URLS.casino}/`
            )
          ) {
            sbRedirect(
              `${SpringConfigs.PAGE_URLS.casino}/${
                SpringConfigs.CASINO_MOUNT_PATH
              }/${category.id}${
                category.id === FAVORITE ? '?provider=all' : ''
              }`,
              false,
              true
            );
            saveCurrentLocation();

            resolve(true);
          } else {
            history.push({
              pathname: `${mountPoint(window.location.pathname, false, true)}/${
                category.id
              }`,
              search: window.location.search
            });
            saveCurrentLocation();

            resolve(true);
          }
        });
      };

      if (
        SpringConfigs.USER_PERSONALIZATION_ENABLED &&
        category.id === CUSTOM_CATEGORIES.favorite.id &&
        !isLoggedIn
      ) {
        openLoginPopup();
        const afterSignIn = new AfterSignIn();

        const afterSigninAction = () => {
          return new Promise(resolve => {
            handleCategoryClick().finally(() => {
              resolve(true);
            });
          });
        };

        afterSignIn.appendAction(
          AFTER_SIGN_IN_ACTION_TYPES.NAVIGATE_TO_MY_CASINO,
          afterSigninAction
        );
      } else {
        handleCategoryClick();
      }
    },
    [history, isLoggedIn]
  );

  const showGamesCount = useMemo(() => {
    return gamesCount || gamesCount === undefined;
  }, [gamesCount]);

  const isNotInCasinoPageAndInSidebar = useMemo(() => {
    return (
      fromSideBar &&
      SpringConfigs.PAGE_URLS.casino &&
      !window.location.href.includes(SpringConfigs.PAGE_URLS.casino)
    );
  }, [fromSideBar, window.location.href]);

  useEffect(() => {
    /** Updates "category" to "all", if it is not set **/
    const { categoryId } = getCasinoParams();

    const isShouldRedirectWithMountPoint =
      !isNotInCasinoPageAndInSidebar &&
      !window.location.href.includes(
        mountPoint(SpringConfigs.PAGE_URLS.casino, false, true)
      );

    if (
      !SpringConfigs.MOCKED_DATA &&
      !categoryId &&
      (!fromSideBar || isShouldRedirectWithMountPoint)
    ) {
      history.push({
        pathname: `${mountPoint(window.location.pathname, false, true)}/${
          CUSTOM_CATEGORIES.all.id
        }`,
        search: window.location.search
      });
    }
    /** **/

    /** Sets category ids in redux store, if provided for element **/

    const catIds = categoryIds ? categoryIds.split(',') : [];
    const customCatIds = categoryCustomIds ? categoryCustomIds.split(',') : [];

    let categories =
      (catIds.length || customCatIds.length) && categoryIds !== 'all'
        ? [
            CUSTOM_CATEGORIES.favorite.id,
            CUSTOM_CATEGORIES.all.id,
            CUSTOM_CATEGORIES.lastPlayed.id,
            ...customCatIds,
            ...catIds
          ]
        : [];

    if (!lastPlayedCatSettings.lastPlayed) {
      categories = categories.filter(
        elem => elem != CUSTOM_CATEGORIES.lastPlayed.id
      );
    }

    const categoriesSet = categories;
    dispatch(
      setCategoryCustomIds([
        CUSTOM_CATEGORIES.favorite.id,
        CUSTOM_CATEGORIES.all.id,
        CUSTOM_CATEGORIES.lastPlayed.id,
        ...customCatIds
      ])
    );

    isMobile() || !fromSideBar
      ? dispatch(setCasinoOriginalCategoriesSet(categoriesSet))
      : dispatch(setCasinoOriginalCategoriesSetLeftSideBar(categoriesSet));
    /** **/
  }, [lastPlayedCatSettings, isNotInCasinoPageAndInSidebar]);

  useDebounce(
    () => {
      if (
        !enhancedParams ||
        !gamesConfigs?.bindWithCategories ||
        !gamesCount ||
        GLOBALS.casino.allGamesCountFetched ||
        fromSideBar
      ) {
        return;
      }

      //need more data in enhancedParams to initiate correct call
      if (
        Object.keys(enhancedParams).length <=
        ENHANCED_PARAMS_CORRECT_KEYS_LENGTH
      ) {
        return;
      }

      getCasinoGames<TNewCasinoDataWithCounts>({
        ...enhancedParams,
        offset: 0,
        limit: 1,
        by_key: ENewCasinoGamesFetchingParamsKey.Id,
        category: enhancedParams._all_categories,
        provider: undefined
      }).then(data => {
        GLOBALS.casino.allGamesCountFetched = true;

        if (Number(data.data.status) === -1) {
          return;
        }

        let totalCount = 0;

        if (data.data.total_count === undefined) {
          totalCount = Object.values(data.data.games || {}).reduce(
            (acc, item) => {
              return acc + Number(item.count);
            },
            0
          );
        } else {
          totalCount = Number(data.data.total_count);
        }

        dispatch(setTotalCount(totalCount.toString()));
      });
    },
    DEBOUNCE_DELAY._500,
    [JSON.stringify(enhancedParams)]
  );

  if (SpringConfigs.MOCKED_DATA) {
    return (
      <Viewport
        mobile={CategoriesMobile}
        desktop={CategoriesDesktop}
        innerProps={{
          layout,
          gamesCount: showGamesCount,
          namesOnly
        }}
      />
    );
  }

  return categories === null ? (
    SkeletonLayout
  ) : !categories.length ? (
    <div>{t('casino.noCategories')}</div>
  ) : (
    <Route
      path={
        isNotInCasinoPageAndInSidebar
          ? ''
          : `${mountPoint(location.pathname, false, true)}/:category?`
      }
    >
      <div
        className="casino-categories-block"
        {...(!fromSideBar && {
          'data-testid': 'casino-categories-block'
        })}
      >
        <Viewport
          mobile={CategoriesMobile}
          desktop={CategoriesDesktop}
          innerProps={{
            theme: THEME,
            onCategoryClick,
            layout,
            gamesCount: showGamesCount,
            fromSideBar,
            isOpen,
            categoryItems,
            namesOnly
          }}
          fallback={SkeletonLayout}
        />
      </div>
    </Route>
  );
};
